'use client';

import Image from 'next/image';
import type { PortableTextBlock } from '@portabletext/types';
import { UiSection } from '@/components/ui/UiSection';
import { UiContainer } from '@/components/ui/UiContainer';
import { UiHeading } from '@/components/ui/UiHeading';
import { UiIcon } from '@/components/ui/UiIcon';
import { UiSliderNavigation, useSlider } from '@/components/ui/patterns/UiSlider';
import { PortableTextRenderer } from '@/components/content';
import { useDictionary } from '@/components/providers';
import { cn } from '@/lib/utils/tw-merge';
import { resolveImageAlt, sanityImage } from '@/lib/sanity/image';
import type { BlockRoadmapItem } from '@/lib/sanity/dto';
import { type HeadingLevel } from '@/lib/utils/headingLevel';

export type RoadmapProps = {
    anchor?: string;
    content?: PortableTextBlock[];
    items?: BlockRoadmapItem[];
    background?: 'page' | 'surface';
    headingLevel?: HeadingLevel;
};

export function Roadmap({ anchor, content, items, background, headingLevel = 3 }: RoadmapProps) {
    const { a11y } = useDictionary();
    const { scrollRef, canScrollLeft, canScrollRight, scrollPrev, scrollNext, onKeyDown } = useSlider<HTMLUListElement>(
        items?.length ?? 0,
    );

    return (
        <UiSection id={anchor} background={background} borderTop="hairline-2">
            <UiContainer>
                <div className="relative mb-[var(--space-intro-56)]">
                    {content ? (
                        <PortableTextRenderer
                            value={content}
                            className={cn(items?.length && '[&>p:last-of-type]:pr-28')}
                        />
                    ) : null}

                    {items?.length ? (
                        <UiSliderNavigation
                            className="absolute bottom-0 right-0"
                            canScrollLeft={canScrollLeft}
                            canScrollRight={canScrollRight}
                            onPrev={scrollPrev}
                            onNext={scrollNext}
                        />
                    ) : null}
                </div>

                {items?.length ? (
                    <div
                        role="region"
                        aria-roledescription="carousel"
                        aria-label={a11y.roadmap}
                        className="relative overflow-hidden"
                    >
                        <div
                            aria-hidden="true"
                            className={cn(
                                'pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-30 bg-linear-to-l from-transparent to-page transition-opacity duration-300 md:block',
                                canScrollLeft ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                        <div
                            aria-hidden="true"
                            className={cn(
                                'pointer-events-none absolute inset-y-0 right-0 z-10 w-14 bg-linear-to-r from-transparent to-page transition-opacity duration-300 md:w-30',
                                canScrollRight ? 'opacity-100' : 'opacity-0',
                            )}
                        />

                        <ul
                            ref={scrollRef}
                            tabIndex={0}
                            onKeyDown={onKeyDown}
                            className="scrollbar-hidden flex snap-x snap-mandatory gap-3.5 overflow-x-auto pb-2 focus-visible:outline-none"
                        >
                            {items.map((item, i) => {
                                const imageSrc = sanityImage.roadmap(item.image);
                                return (
                                    <li
                                        key={item._key ?? i}
                                        className="flex w-70 shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-dashed border-hairline-3 bg-surface"
                                    >
                                        {imageSrc ? (
                                            <div className="relative aspect-video w-full overflow-hidden bg-surface-3">
                                                <Image
                                                    src={imageSrc}
                                                    alt={resolveImageAlt(item.image, item.title)}
                                                    fill
                                                    sizes="280px"
                                                    className="object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="aspect-video w-full bg-surface-3" aria-hidden="true" />
                                        )}
                                        <div className="flex flex-col items-start gap-3 p-5">
                                            <div className="flex w-full items-center justify-between gap-3">
                                                <div className="flex size-9 items-center justify-center rounded-lg bg-surface-3 text-copy-muted">
                                                    {item.icon ? <UiIcon name={item.icon} size={18} /> : null}
                                                </div>
                                                {item.badge ? (
                                                    <span
                                                        className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                                                        style={{
                                                            background: 'var(--pg-badge-amber-bg)',
                                                            color: 'var(--pg-badge-amber-text)',
                                                            borderColor: 'var(--pg-badge-amber-border)',
                                                        }}
                                                    >
                                                        {item.badge}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {item.title ? (
                                                <UiHeading
                                                    level={headingLevel}
                                                    className="text-base font-medium text-copy"
                                                >
                                                    {item.title}
                                                </UiHeading>
                                            ) : null}
                                            {item.description ? (
                                                <p className="text-sm leading-relaxed text-copy-muted">
                                                    {item.description}
                                                </p>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ) : null}
            </UiContainer>
        </UiSection>
    );
}
